/**
 * Dashboard Service
 * Handles data fetching and aggregation for the dashboard
 */

import { graphql, graphqlOperation } from "./AmplifyServices";
import { clientChargeByCompanyDashboard } from "../queries/DashboardQueries";
import {
    getDateRangeForPeriod,
    processClientCharges,
    aggregateClientChargesByMonth,
    combineMonthlyData,
    filterRecordsByDateRange,
    calculateGrowth
} from "../utils/dashboardUtils";

// ============================================================================
// PAGINATION HELPER
// ============================================================================

/**
 * Fetches all records from a paginated GraphQL query.
 * Continues fetching until nextToken is null or undefined.
 *
 * @param {string} query - GraphQL query string
 * @param {Object} variables - Query variables
 * @param {string} queryName - Name of the query field in response
 * @returns {Promise<Array>} - All fetched records
 */
async function fetchAllPaginatedRecords(query, variables, queryName) {
    let allItems = [];
    let nextToken = null;
    const limit = 1000; // Fetch in batches of 1000

    do {
        try {
            const response = await graphql(
                graphqlOperation(query, {
                    ...variables,
                    limit,
                    nextToken
                })
            );

            const data = response.data[queryName];
            if (data && data.items) {
                allItems = [...allItems, ...data.items];
            }
            nextToken = data?.nextToken;
        } catch (error) {
            console.error(
                `Error fetching paginated records for ${queryName}:`,
                error
            );
            throw error;
        }
    } while (nextToken);

    return allItems;
}

// ============================================================================
// DATA FETCHING FUNCTIONS
// ============================================================================

/**
 * Fetches ClientCharge records for a specific date range using 'between' query
 *
 * @param {string} companyId - Company ID
 * @param {string} startDateISO - Start date ISO string (e.g., "2026-02-03T05:00:00.000Z")
 * @param {string} endDateISO - End date ISO string (e.g., "2026-02-03T15:00:00.000Z")
 * @returns {Promise<Array>}
 */
async function fetchClientCharges(companyId, startDateISO, endDateISO) {
    const variables = {
        companyId,
        createdAtId: {
            between: [
                { createdAt: startDateISO },
                { createdAt: endDateISO }
            ]
        }
    };

    return fetchAllPaginatedRecords(
        clientChargeByCompanyDashboard,
        variables,
        "clientChargeByCompany"
    );
}

// ============================================================================
// PERIOD DATA PROCESSING
// ============================================================================

/**
 * Fetches and processes data for a specific time period
 *
 * @param {string} companyId - Company ID
 * @param {'today' | 'currentMonth' | 'ytd' | 'lastYear'} period - Time period
 * @param {string} timezone - IANA timezone string (e.g., "America/Toronto")
 * @returns {Promise<Object>}
 */
async function fetchPeriodData(companyId, period, timezone) {
    const dateRange = getDateRangeForPeriod(period, timezone);

    // Fetch ClientCharge data (now includes tips and additional charges)
    const clientCharges = await fetchClientCharges(
        companyId,
        dateRange.startDateISO,
        dateRange.endDateISO
    );

    // NOTE: The following client-side filtering is redundant when using 'between'
    // queries with precise UTC boundaries. It is kept as a safety net for edge
    // cases where the query might return records slightly outside the expected
    // range due to timezone conversion nuances.
    let filteredClientCharges = clientCharges;

    if (period === "today" || period === "currentMonth") {
        filteredClientCharges = filterRecordsByDateRange(
            clientCharges,
            dateRange.startDate,
            dateRange.endDate,
            timezone
        );
    }

    // Process the data - now returns tips and additional charges from ClientCharge
    const clientChargeData = processClientCharges(filteredClientCharges);

    // Build additionalCharges from processClientCharges result for backward compatibility
    const additionalChargeData = {
        tips: clientChargeData.tips,
        nonTipCharges: clientChargeData.nonTipCharges,
        tipsByCurrency: clientChargeData.tipsByCurrency,
        additionalChargesByCurrency: clientChargeData.additionalChargesByCurrency
    };

    return {
        period,
        dateRange,
        clientCharges: clientChargeData,
        additionalCharges: additionalChargeData,
        rawClientCharges: clientCharges // Keep for monthly aggregation
    };
}

// ============================================================================
// MAIN DASHBOARD DATA FETCHING
// ============================================================================

/**
 * Fetches all dashboard data for a company
 *
 * @param {string} companyId - Company ID
 * @param {string} timezone - IANA timezone string (e.g., "America/Toronto")
 * @returns {Promise<Object>} - Complete dashboard data
 */
export async function fetchDashboardData(companyId, timezone) {
    if (!companyId) {
        throw new Error("Company ID is required");
    }

    // Default timezone if not provided
    const tz = timezone || "America/New_York";

    // Fetch all periods in parallel
    const [todayData, currentMonthData, ytdData, lastYearData] =
        await Promise.all([
            fetchPeriodData(companyId, "today", tz),
            fetchPeriodData(companyId, "currentMonth", tz),
            fetchPeriodData(companyId, "ytd", tz),
            fetchPeriodData(companyId, "lastYear", tz)
        ]);

    // Generate monthly data for charts (YTD and Last Year)
    const currentYear = new Date().getFullYear();
    const lastYear = currentYear - 1;

    // Monthly aggregation for YTD (now includes tips and additional charges)
    const ytdClientChargeMonthly = aggregateClientChargesByMonth(
        ytdData.rawClientCharges,
        currentYear,
        tz
    );
    const ytdMonthlyData = combineMonthlyData(ytdClientChargeMonthly);

    // Monthly aggregation for Last Year (now includes tips and additional charges)
    const lastYearClientChargeMonthly = aggregateClientChargesByMonth(
        lastYearData.rawClientCharges,
        lastYear,
        tz
    );
    const lastYearMonthlyData = combineMonthlyData(lastYearClientChargeMonthly);

    // Calculate growth metrics (YTD vs Last Year)
    // Net = (Booked + Additional) - Refunds
    const ytdBooked = Object.values(ytdData.clientCharges.byCurrency).reduce(
        (sum, curr) => sum + (curr.booked || 0),
        0
    );
    const ytdRefunds = Object.values(ytdData.clientCharges.byCurrency).reduce(
        (sum, curr) => sum + (curr.refunds || 0),
        0
    );
    const ytdAdditional = ytdData.clientCharges.nonTipCharges || 0;
    const ytdTotalNet = (ytdBooked + ytdAdditional) - ytdRefunds;

    const lastYearBooked = Object.values(
        lastYearData.clientCharges.byCurrency
    ).reduce((sum, curr) => sum + (curr.booked || 0), 0);
    const lastYearRefunds = Object.values(
        lastYearData.clientCharges.byCurrency
    ).reduce((sum, curr) => sum + (curr.refunds || 0), 0);
    const lastYearAdditional = lastYearData.clientCharges.nonTipCharges || 0;
    const lastYearTotalNet = (lastYearBooked + lastYearAdditional) - lastYearRefunds;

    const revenueGrowth = calculateGrowth(ytdTotalNet, lastYearTotalNet);
    const ordersGrowth = calculateGrowth(
        ytdData.clientCharges.totalOrders,
        lastYearData.clientCharges.totalOrders
    );
    const clientsGrowth = calculateGrowth(
        ytdData.clientCharges.totalClients,
        lastYearData.clientCharges.totalClients
    );

    return {
        today: {
            clientCharges: todayData.clientCharges,
            additionalCharges: todayData.additionalCharges
        },
        currentMonth: {
            clientCharges: currentMonthData.clientCharges,
            additionalCharges: currentMonthData.additionalCharges
        },
        ytd: {
            clientCharges: ytdData.clientCharges,
            additionalCharges: ytdData.additionalCharges,
            monthlyData: ytdMonthlyData
        },
        lastYear: {
            clientCharges: lastYearData.clientCharges,
            additionalCharges: lastYearData.additionalCharges,
            monthlyData: lastYearMonthlyData
        },
        growth: {
            revenue: revenueGrowth,
            orders: ordersGrowth,
            clients: clientsGrowth
        },
        metadata: {
            currentYear,
            lastYear,
            timezone: tz,
            fetchedAt: new Date().toISOString()
        }
    };
}

// ============================================================================
// CHART DATA PREPARATION
// ============================================================================

/**
 * Prepares data for the comparison bar chart with stacked revenue components
 *
 * @param {Array} ytdMonthlyData - YTD monthly data
 * @param {Array} lastYearMonthlyData - Last year monthly data
 * @param {number} currentYear - Current year
 * @param {number} lastYear - Last year
 * @returns {Array} - Chart-ready data with year-keyed fields for stacking
 */
export function prepareComparisonChartData(
    ytdMonthlyData,
    lastYearMonthlyData,
    currentYear,
    lastYear
) {
    return ytdMonthlyData.map((ytdMonth, index) => {
        const lastYearMonth = lastYearMonthlyData[index] || {
            booked: 0,
            refunds: 0,
            netBooked: 0,
            additional: 0,
            net: 0
        };

        return {
            month: ytdMonth.monthName,
            // YTD raw values for tooltip
            ytdBooked: ytdMonth.booked || 0,
            ytdRefunds: ytdMonth.refunds || 0,
            ytdNetBooked: ytdMonth.netBooked || 0,
            ytdAdditional: ytdMonth.additional || 0,
            ytdNet: ytdMonth.net || 0,
            // Last Year raw values for tooltip
            lastYearBooked: lastYearMonth.booked || 0,
            lastYearRefunds: lastYearMonth.refunds || 0,
            lastYearNetBooked: lastYearMonth.netBooked || 0,
            lastYearAdditional: lastYearMonth.additional || 0,
            lastYearNet: lastYearMonth.net || 0,
            // Year-keyed fields for chart bar stacking
            [`${currentYear}_netBooked`]: ytdMonth.netBooked || 0,
            [`${currentYear}_additional`]: ytdMonth.additional || 0,
            [`${lastYear}_netBooked`]: lastYearMonth.netBooked || 0,
            [`${lastYear}_additional`]: lastYearMonth.additional || 0,
            // Total net for each year (for legacy compatibility)
            [currentYear]: ytdMonth.net || 0,
            [lastYear]: lastYearMonth.net || 0
        };
    });
}
